(function(window) {
    'use strict';

    const DEFAULT_SETTINGS = {
        show_image: 'yes',
        show_title: 'yes',
        show_price: 'yes',
        show_rating: 'no',
        show_sku: 'no',
        show_excerpt: 'no',
        show_add_to_cart: 'yes',
        show_sale_badge: 'yes',
        image_size: 'woocommerce_thumbnail',
        excerpt_length: 15,
        button_icon_position: 'left',
    };

    const DEFAULT_I18N = {
        sale_badge: 'Sale',
        sku_label: 'SKU:',
        no_results: 'No products found matching your filters.',
        out_of_stock: 'Out of stock',
        add_to_cart: 'Add to Cart',
    };

    const ICON_HTML = '<i class="fas fa-shopping-cart" aria-hidden="true"></i>';

    function normalizeSettings(settings) {
        return Object.assign({}, DEFAULT_SETTINGS, settings || {});
    }

    function normalizeI18n(i18n) {
        return Object.assign({}, DEFAULT_I18N, i18n || {});
    }

    function escapeHtml(value) {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    function renderImage(product, i18n) {
        if (!product.image || !product.image.url) {
            return '';
        }

        const image = product.image;

        const attributes = [
            `src="${escapeHtml(image.url)}"`,
            `alt="${escapeHtml(image.alt || product.title)}"`,
            `title="${escapeHtml(product.title)}"`,
            'loading="lazy"',
        ];

        if (image.width) {
            attributes.push(`width="${image.width}"`);
        }
        if (image.height) {
            attributes.push(`height="${image.height}"`);
        }
        if (image.srcset) {
            attributes.push(`srcset="${escapeHtml(image.srcset)}"`);
        }
        if (image.sizes) {
            attributes.push(`sizes="${escapeHtml(image.sizes)}"`);
        }

        return `<a href="${escapeHtml(product.permalink)}"><img ${attributes.join(' ')} /></a>`;
    }

    function renderBadge(product, settings, i18n) {
        if (settings.show_sale_badge !== 'yes' || !product.on_sale) {
            return '';
        }

        if (settings.sale_badge_content_type === 'percentage') {
            const discount = Math.min(100, Math.max(0, Math.round(Number(product.discount_percentage) || 0)));
            if (discount > 0) {
                return `<span class="sale-badge">-${discount}%</span>`;
            }
        }

        return `<span class="sale-badge">${escapeHtml(i18n.sale_badge)}</span>`;
    }

    function renderTitle(product, settings) {
        if (settings.show_title !== 'yes') {
            return '';
        }

        // Get responsive title limits
        const maxLengthDesktop = parseInt(settings.title_max_length) || 0;
        const maxLengthTablet = parseInt(settings.title_max_length_tablet) || maxLengthDesktop;
        const maxLengthMobile = parseInt(settings.title_max_length_mobile) || maxLengthTablet;
        
        // Use desktop limit as fallback for initial render
        const maxLength = maxLengthDesktop;
        let titleText = product.title;
        
        // Truncate title if max length is set
        if (maxLength > 0 && titleText.length > maxLength) {
            titleText = titleText.substring(0, maxLength) + '...';
        }

        return `<h3 class="product-title" ` +
            `data-full-title="${escapeHtml(product.title)}" ` +
            `data-max-length-desktop="${maxLengthDesktop}" ` +
            `data-max-length-tablet="${maxLengthTablet}" ` +
            `data-max-length-mobile="${maxLengthMobile}">` +
            `<a href="${escapeHtml(product.permalink)}" title="${escapeHtml(product.title)}">${escapeHtml(titleText)}</a></h3>`;
    }

    function renderSku(product, settings, i18n) {
        if (settings.show_sku !== 'yes' || !product.sku) {
            return '';
        }

        return `<div class="product-sku">${escapeHtml(i18n.sku_label)} ${escapeHtml(product.sku)}</div>`;
    }

    function renderRating(product, settings) {
        if (settings.show_rating !== 'yes' || !product.rating_html) {
            return '';
        }

        return `<div class="product-rating">${product.rating_html}</div>`;
    }

    function renderExcerpt(product, settings) {
        if (settings.show_excerpt !== 'yes' || !product.excerpt) {
            return '';
        }

        return `<div class="product-excerpt">${escapeHtml(product.excerpt)}</div>`;
    }

    function renderPrice(product, settings) {
        if (settings.show_price !== 'yes' || !product.price_html) {
            return '';
        }

        return `<div class="product-price">${product.price_html}</div>`;
    }

    function renderAddToCart(product, settings, labels) {
        if (settings.show_add_to_cart !== 'yes') {
            return '';
        }

        const iconPosition = settings.button_icon_position === 'right' ? 'right' : 'left';

        const buttonText = product.add_to_cart && product.add_to_cart.text
            ? product.add_to_cart.text
            : labels.add_to_cart;
        const isExplicitlyInStock = product.is_in_stock !== undefined ? !!product.is_in_stock : true;
        const stockStatus = (product.stock_status || '').toLowerCase();
        const isOutOfStock = !isExplicitlyInStock || stockStatus === 'outofstock';
        const isPurchasable = product.is_purchasable === undefined ? true : !!product.is_purchasable;
        const isDisabled = isOutOfStock || !isPurchasable;
        // Matches ProductCardRenderer: no disabled "out of stock" button when the indicator is suppressed
        if (isDisabled && product.hide_out_of_stock_indicator) {
            return '';
        }
        const iconSpan = `<span>${escapeHtml(buttonText)}</span>`;

        const contents = iconPosition === 'left'
            ? `${ICON_HTML}${iconSpan}`
            : `${iconSpan}${ICON_HTML}`;

        return `
            <div class="product-actions">
                <button type="button"
                        class="product-add-to-cart ajax_add_to_cart icon-${iconPosition}${isDisabled ? ' disabled out-of-stock' : ''}"
                        data-product_id="${escapeHtml(product.add_to_cart?.product_id || product.id)}"
                        data-product_sku="${escapeHtml(product.add_to_cart?.sku || '')}"
                        data-quantity="1"
                        ${isDisabled ? 'disabled aria-disabled="true"' : ''}>
                    ${contents}
                </button>
            </div>
        `;
    }

    function renderCard(product, settings, i18n) {
        const imageSection = settings.show_image === 'yes'
            ? `<div class="product-image">
                    ${renderBadge(product, settings, i18n)}
                    ${renderImage(product, i18n)}
               </div>`
            : '';

        const infoSection = `
            <div class="product-info">
                ${renderTitle(product, settings)}
                ${renderSku(product, settings, i18n)}
               ${renderRating(product, settings)}
               ${renderExcerpt(product, settings)}
               ${renderPrice(product, settings)}
               ${renderAddToCart(product, settings, i18n)}
            </div>
        `;

        return `<div class="product-card" data-product-id="${escapeHtml(product.id)}">${imageSection}${infoSection}</div>`;
    }

    function renderProducts(products, settings, i18n) {
        const normalizedSettings = normalizeSettings(settings);
        const labels = normalizeI18n(i18n);

        if (!products || !products.length) {
            return `<div class="woocommerce-no-products-found" style="grid-column: 1 / -1; text-align: center; padding: 40px 20px;"><p style="margin: 0; color: #6b7280;">${escapeHtml(labels.no_results)}</p></div>`;
        }

        return products.map(product => renderCard(product, normalizedSettings, labels)).join('');
    }

    window.StoredashProductRenderer = {
        defaults: DEFAULT_SETTINGS,
        renderProducts,
        normalizeSettings,
    };
})(window);

